import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import AccountsSubNav from '../components/AccountsSubNav'
import SharedFilters from '../../../../components/SharedFilters'
import AppActionMenu from '../../../../components/AppActionMenu'
import AppPagination from '../../../../components/AppPagination'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { getEmployees, deleteEmployee, archiveEmployee } from '../../../payroll/employee/api/employeeApi'
import api from '../../../../api/client'

// Gmail-style indeterminate checkbox
function IndeterminateCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 rounded border-2 border-outline-variant/50 bg-surface-container-low accent-primary cursor-pointer transition"
    />
  )
}

export default function EmployeeListScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const isPayroll = location.pathname.startsWith('/payroll')
  const getEntryPath = (id) => `${isPayroll ? '/payroll/employee/entry' : '/masters/acheads/employees/entry'}?id=${id}`

  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [departmentFilter, setDepartmentFilter] = useState('All')
  const [placeFilter, setPlaceFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('Active')
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 300)
    return () => clearTimeout(handler)
  }, [searchText])

  // Options state
  const [companyOptions, setCompanyOptions] = useState([{ value: 'All', label: 'All Companies' }])
  const [branchOptions, setBranchOptions] = useState([{ value: 'All', label: 'All Branches' }])
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'All', label: 'All Departments' }])
  const [placeOptions, setPlaceOptions] = useState([{ value: 'All', label: 'All Places' }])

  // Data state
  const [items, setItems] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set())

  // Load dropdown options on mount
  useEffect(() => {
    let isMounted = true
    const loadOptions = async () => {
      try {
        const [compRes, branchRes, deptRes] = await Promise.all([
          api.get('/options/companies').catch(() => ({ data: [] })),
          api.get('/options/branches').catch(() => ({ data: [] })),
          api.get('/options/departments').catch(() => ({ data: [] })),
        ])
        if (!isMounted) return
        if (Array.isArray(compRes.data)) {
          setCompanyOptions([
            { value: 'All', label: 'All Companies' },
            ...compRes.data.map((c) => ({ value: String(c.id), label: c.name || c.code })),
          ])
        }
        if (Array.isArray(branchRes.data)) {
          setBranchOptions([
            { value: 'All', label: 'All Branches' },
            ...branchRes.data.map((b) => ({ value: String(b.id), label: b.name || b.code })),
          ])
        }
        if (Array.isArray(deptRes.data)) {
          setDepartmentOptions([
            { value: 'All', label: 'All Departments' },
            ...deptRes.data.map((d) => ({ value: String(d.id), label: d.name || d.code })),
          ])
        }
      } catch (err) {
        console.error('Failed to load filter options', err)
      }
    }
    loadOptions()
    return () => {
      isMounted = false
    }
  }, [])

  // Fetch employees from backend
  const fetchEmployees = useCallback(async (signal) => {
    setIsLoading(true)
    try {
      const params = {
        page,
        page_size: 10,
        status: statusFilter,
      }
      if (debouncedSearchText.trim()) {
        params.search = debouncedSearchText.trim()
        params.search_field = searchField.toLowerCase()
      }
      if (companyFilter !== 'All') {
        params.company_id = Number(companyFilter)
      }
      if (branchFilter !== 'All') {
        params.branch_id = Number(branchFilter)
      }
      if (departmentFilter !== 'All') {
        params.department_id = Number(departmentFilter)
      }

      const data = await getEmployees(params, { signal })
      setItems(data.items || [])
      setTotalCount(data.total_count || 0)
      setTotalPages(data.total_pages || 1)
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return
      }
      console.error(err)
      appToast.error('Failed to load employees.')
    } finally {
      setIsLoading(false)
    }
  }, [page, debouncedSearchText, searchField, companyFilter, branchFilter, departmentFilter, statusFilter])

  useEffect(() => {
    const controller = new AbortController()
    fetchEmployees(controller.signal)
    return () => controller.abort()
  }, [fetchEmployees])

  const handleReset = () => {
    setCompanyFilter('All')
    setBranchFilter('All')
    setDepartmentFilter('All')
    setPlaceFilter('All')
    setStatusFilter('Active')
    setSearchField('Name')
    setSearchText('')
    setDebouncedSearchText('')
    setPage(1)
    setSelectedIds(new Set())
  }

  const handleArchive = async (id, currentArchive) => {
    try {
      await archiveEmployee(id, !currentArchive)
      appToast.success(`Employee ${currentArchive ? 'unarchived' : 'archived'} successfully.`)
      fetchEmployees()
    } catch (err) {
      appToast.error('Failed to update employee archive status.')
    }
  }

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Employee',
      message: `Delete Employee "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteEmployee(id)
      setSelectedIds((prev) => {
        const n = new Set(prev)
        n.delete(id)
        return n
      })
      appToast.success(`Employee "${name}" deleted.`)
      fetchEmployees()
    } catch (err) {
      appToast.error('Failed to delete employee.')
    }
  }

  // Selection helpers
  const allIds = items.map((i) => i.id)
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id))
  const someSelected = allIds.some((id) => selectedIds.has(id)) && !allSelected
  const selectedCount = allIds.filter((id) => selectedIds.has(id)).length

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const n = new Set(prev)
        allIds.forEach((id) => n.delete(id))
        return n
      })
    } else {
      setSelectedIds((prev) => {
        const n = new Set(prev)
        allIds.forEach((id) => n.add(id))
        return n
      })
    }
  }

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  // Bulk actions
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    const confirmed = await appConfirm({
      title: 'Bulk Delete Employees',
      message: `Delete ${ids.length} selected employee(s)? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await Promise.all(ids.map((id) => deleteEmployee(id)))
      setSelectedIds(new Set())
      appToast.success(`${ids.length} employee(s) deleted.`)
      fetchEmployees()
    } catch (err) {
      appToast.error('Failed to delete selected employees.')
    }
  }

  const handleBulkArchive = async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    try {
      await Promise.all(ids.map((id) => archiveEmployee(id, true)))
      appToast.success(`${ids.length} employee(s) updated.`)
      setSelectedIds(new Set())
      fetchEmployees()
    } catch (err) {
      appToast.error('Failed to archive selected employees.')
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            {isPayroll ? 'Payroll Suite' : 'Financial Master'}
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Employees
          </h1>
        </div>
        {!isPayroll && <AccountsSubNav />}
      </section>

      {/* Shared Filters Accordion */}
      <SharedFilters
        searchField={searchField}
        searchText={searchText}
        statusFilter={statusFilter}
        companyFilter={companyFilter}
        branchFilter={branchFilter}
        departmentFilter={departmentFilter}
        placeFilter={placeFilter}

        setSearchField={setSearchField}
        setSearchText={(val) => {
          setSearchText(val)
          setPage(1)
        }}
        setStatusFilter={(val) => {
          setStatusFilter(val)
          setPage(1)
        }}
        setCompanyFilter={(val) => {
          setCompanyFilter(val)
          setPage(1)
        }}
        setBranchFilter={(val) => {
          setBranchFilter(val)
          setPage(1)
        }}
        setDepartmentFilter={(val) => {
          setDepartmentFilter(val)
          setPage(1)
        }}
        setPlaceFilter={(val) => {
          setPlaceFilter(val)
          setPage(1)
        }}
        onReset={handleReset}

        searchFieldOptions={[
          { value: 'Name', label: 'Name' },
          { value: 'Code', label: 'Code' },
          { value: 'Mobile', label: 'Mobile' },
          { value: 'Email', label: 'Email' },
        ]}
        statusOptions={[
          { value: 'Active', label: 'Active' },
          { value: 'Inactive', label: 'Archived' },
          { value: 'All', label: 'All' },
        ]}
        companyOptions={companyOptions}
        branchOptions={branchOptions}
        departmentOptions={departmentOptions}
        placeOptions={placeOptions}
        visibleAccordionFilters={['company', 'branch', 'department', 'place']}
        tools={[
          { key: 'org-chart', label: 'Organizational Chart', icon: 'schema' },
          { key: 'import', label: 'Import Employees', icon: 'upload' },
          { key: 'salary-structure', label: 'Salary Structure', icon: 'payments' },
          { key: 'print-id', label: 'Print ID Card', icon: 'badge' },
        ]}
      />

      {/* Data Table */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        {/* Gmail-style Bulk Selection Action Bar */}
        {selectedCount > 0 && (
          <div className="flex items-center justify-between gap-4 border-b border-primary/20 bg-primary/[0.06] px-5 py-3">
            <div className="flex items-center gap-3">
              <IndeterminateCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
              />
              <span className="text-xs font-bold text-primary">
                {selectedCount} selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-on-surface-variant hover:text-primary underline cursor-pointer"
              >
                Clear
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleBulkArchive}
                className="flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-low transition cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">archive</span>
                Archive
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-500/20 transition cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">delete</span>
                Delete
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
                {/* Select All checkbox in header */}
                <th className="w-10 pl-5 pr-2 py-3.5">
                  <IndeterminateCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-5 py-3.5">Name</th>
                <th className="px-5 py-3.5">Mobile</th>
                <th className="px-5 py-3.5">Email</th>
                <th className="px-5 py-3.5 text-right">CTC</th>
                <th className="px-5 py-3.5">Reporting To</th>
                <th className="w-20 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    Loading employees...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    No entries meet selected criteria.
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const isChecked = selectedIds.has(item.id)
                  const isArchived = Boolean(item.archive)
                  return (
                    <tr
                      key={item.id}
                      className={`transition-colors hover:bg-surface-container-low/40 ${isChecked ? 'bg-primary/[0.04]' : ''}`}
                    >
                      {/* Per-row checkbox */}
                      <td className="w-10 pl-5 pr-2 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelectOne(item.id)}
                          className="h-4 w-4 rounded border-2 border-outline-variant/50 bg-surface-container-low accent-primary cursor-pointer"
                        />
                      </td>
                      <td
                        className="px-5 py-3.5 font-bold text-on-surface cursor-pointer hover:text-primary transition-colors"
                        onClick={() => navigate(getEntryPath(item.id))}
                      >
                        {item.name}
                        {item.code ? (
                          <span className="ml-2 text-xs font-normal text-on-surface-variant">
                            ({item.code})
                          </span>
                        ) : null}
                      </td>
                      <td className="px-5 py-3.5 text-on-surface-variant">{item.mobile || '—'}</td>
                      <td className="px-5 py-3.5 text-on-surface-variant font-mono text-xs">{item.email || '—'}</td>
                      <td className="px-5 py-3.5 text-right font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">
                        ₹{Number(item.ctc || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-5 py-3.5 text-secondary font-semibold">{item.manager_name || '—'}</td>
                      <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <AppActionMenu
                          triggerLabel=""
                          triggerIcon="more_vert"
                          showChevron={false}
                          triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                          items={[
                            { key: 'edit', label: 'Edit', icon: 'edit' },
                            {
                              key: 'archive',
                              label: isArchived ? 'Unarchive' : 'Archive',
                              icon: isArchived ? 'unarchive' : 'archive',
                            },
                            { key: 'delete', label: 'Delete', icon: 'delete' },
                            { key: 'timesheet-analytics', label: 'TimeSheet Analytics', icon: 'monitoring' },
                            { key: 'additional-info', label: 'Additional Info', icon: 'info' },
                            { key: 'availability', label: 'Availability', icon: 'event_available' },
                            { key: 'coursewise-payment', label: 'CourseWise Payment', icon: 'payments' },
                          ]}
                          onSelect={(key) => {
                            if (key === 'edit') navigate(getEntryPath(item.id))
                            else if (key === 'archive') handleArchive(item.id, isArchived)
                            else if (key === 'delete') handleDelete(item.id, item.name)
                            else if (key === 'timesheet-analytics') navigate(`/payroll/attendance-history?empId=${item.id}&empName=${encodeURIComponent(item.name || '')}`)
                            else if (key === 'additional-info') navigate(`${getEntryPath(item.id)}&tab=additional`)
                            else if (key === 'availability') navigate(`${getEntryPath(item.id)}&tab=availability`)
                            else if (key === 'coursewise-payment') navigate(`${getEntryPath(item.id)}&tab=rates`)
                          }}
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {items.length} of {totalCount} entries
            {selectedCount > 0 && (
              <span className="ml-2 font-bold text-primary">• {selectedCount} selected</span>
            )}
          </p>
          <AppPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onPrevious={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            canPrevious={page > 1}
            canNext={page < totalPages}
          />
        </div>
      </section>
    </div>
  )
}
